import { useTranslation } from 'react-i18next';

import type { EmailSettings as TEmailSettings } from '~/types/user';

import { useUpdateEmailSettings, useUser } from '~/api/user';
import { toast } from '~/utils/toast';

import { FormSwitchField } from '../../components/FormSwitchField';
import { listStyle } from './EmailSettings.css';

const emailSettingOrder = {
  invitationReminder: 1,
  nettoSaved: 2,
  bcc: 3,
  followUpEnabled: 4,
  jobPostUpdates: 5,
} as const satisfies Record<keyof TEmailSettings, number>;

const emailSettingKeys = (Object.keys(emailSettingOrder) as (keyof TEmailSettings)[]).sort(
  (a, b) => emailSettingOrder[a] - emailSettingOrder[b],
);

export const EmailSettings = () => {
  const { data: user } = useUser();
  const mutation = useUpdateEmailSettings();

  const { t } = useTranslation(['common', 'settings']);

  const saveEmailSetting = <K extends keyof TEmailSettings>(settingKey: K, value: TEmailSettings[K]) => {
    mutation.mutate(
      { emailSettings: { ...user.emailSettings, [settingKey]: value } },
      { onError: () => toast.danger({ description: t('common:error') }) },
    );
  };

  return (
    <div className={listStyle}>
      {emailSettingKeys.map((emailSetting) => (
        <FormSwitchField
          checked={user.emailSettings[emailSetting]}
          description={t(`settings:general.email.${emailSetting}.description`)}
          key={emailSetting}
          onCheckedChange={(checked) => saveEmailSetting(emailSetting, checked)}
        >
          {t(`settings:general.email.${emailSetting}.title`)}
        </FormSwitchField>
      ))}
    </div>
  );
};
